import React, { useMemo } from "react";

import usePermissions from "../../../hooks/usePermissions";
import { GlobalSearch } from "./GlobalSearch";
import { HeaderOverflowMenu } from "./HeaderOverflowMenu";
import type { HeaderContainerProps } from "./HeaderContainer";

export const HeaderContainerV2 = ({
  start,
  middle,
  end,
  featureSentimentName
}: HeaderContainerProps) => {
  const { roles, capabilities } = usePermissions();

  const showGlobalSearch = useMemo(() => {
    if (roles.derived.hasRoles) {
      return roles.derived.userIsInternal || roles.booking.update;
    }
    const isInternal = capabilities.derived.userIsInternal;
    const hasBookingManagement =
      capabilities.manageVenueGroupBookings.thisVenueGroup ||
      capabilities.manageVenueBookings.some;
    const hasBookingConfirmation =
      capabilities.confirmVenueGroupBookings.thisVenueGroup ||
      capabilities.confirmVenueBookings.some;
    return isInternal || hasBookingManagement || hasBookingConfirmation;
  }, [roles, capabilities]);

  const slot = "flex items-center gap-2 h-full self-stretch";

  // Natural left-to-right flow: LEFT (brand) and MIDDLE (date stepper) sit
  // at their intrinsic sizes next to each other, a flex-1 spacer absorbs
  // remaining width, and RIGHT (venue selector + search + overflow) is
  // pinned to the edge with the venue selector immediately left of search.
  return (
    <div
      className="HeaderContainer HeaderContainer--v2 flex-[1_0_auto] self-stretch flex flex-row flex-nowrap items-center bg-page-background text-heading-color pt-3 gap-3 px-3"
      data-variant="v2"
    >
      {/* left: hamburger + brand */}
      <div className={`${slot} shrink-0 justify-start`} data-slot="left">
        {start || ""}
      </div>

      {/* middle: today + date stepper, flows after left */}
      <div className={`${slot} shrink-0`} data-slot="middle">
        {middle || ""}
      </div>

      {/* spacer pushes right cluster to the edge */}
      <div className="flex-1" aria-hidden="true" />

      {/* right: venue selector → search magnifier → overflow
          (overflow hidden in portrait — its contents live in the drawer) */}
      <div className={`${slot} shrink-0 justify-end`} data-slot="right">
        {end || ""}
        {showGlobalSearch && <GlobalSearch />}
        <div className="portrait:hidden">
          <HeaderOverflowMenu featureSentimentName={featureSentimentName} />
        </div>
      </div>
    </div>
  );
};
